/**
 * Product Service
 *
 * API calls for product management
 */

import { api } from '../lib/api';

// Types
export interface Product {
  id: string;
  name: string;
  slug: string;
  sku: string;
  description: string | null;
  categoryId: string;
  samplePrice: number | null;
  isHotSelling: boolean;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
  updatedAt: string;
  category: {
    id: string;
    name: string;
    slug: string;
  };
  images: ProductImage[];
  pricing: ProductPricing[];
}

export interface ProductListItem {
  id: string;
  name: string;
  slug: string;
  sku: string;
  samplePrice: number | null;
  isHotSelling: boolean;
  status: 'ACTIVE' | 'INACTIVE';
  stockStatus: 'IN_STOCK' | 'OUT_OF_STOCK';
  categoryId: string;
  categoryName: string;
  categorySlug: string;
  primaryImage: string | null;
  startingPrice: number | null;
}

export interface ProductImage {
  id: string;
  imageUrl: string;
  isPrimary: boolean;
  colorVariant: string | null;
  displayOrder: number;
}

export interface ProductPricing {
  id: string;
  quantityTier: string;
  unitPrice: number;
}

export interface ListProductsParams {
  page?: number;
  limit?: number;
  categoryId?: string;
  status?: string;
  stockStatus?: string;
  isHotSelling?: boolean;
  search?: string;
  sortBy?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateProductInput {
  name: string;
  slug: string;
  sku: string;
  description?: string;
  categoryId: string;
  samplePrice?: number;
  isHotSelling?: boolean;
  status?: 'ACTIVE' | 'INACTIVE';
  images?: Array<{
    imageUrl: string;
    isPrimary?: boolean;
    colorVariant?: string;
    displayOrder?: number;
  }>;
  pricing?: Array<{
    quantityTier: string;
    unitPrice: number;
  }>;
}

export interface UpdateProductInput {
  name?: string;
  slug?: string;
  sku?: string;
  description?: string;
  categoryId?: string;
  samplePrice?: number;
  isHotSelling?: boolean;
  status?: 'ACTIVE' | 'INACTIVE';
}

export const productService = {
  /**
   * Get paginated list of products
   */
  list: async (params: ListProductsParams = {}): Promise<PaginatedResponse<ProductListItem>> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.categoryId) searchParams.append('categoryId', params.categoryId);
    if (params.status) searchParams.append('status', params.status);
    if (params.stockStatus) searchParams.append('stockStatus', params.stockStatus);
    if (params.isHotSelling !== undefined) searchParams.append('isHotSelling', params.isHotSelling.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);

    const response = await api.get<ProductListItem[]>(
      `/admin/products?${searchParams.toString()}`
    );

    // Map API response to expected format (API returns data + meta, not pagination)
    const meta = (response as any).meta;
    return {
      data: response.data || [],
      pagination: {
        page: meta?.page || params.page || 1,
        limit: meta?.limit || params.limit || 10,
        total: meta?.total || 0,
        totalPages: meta?.total ? Math.ceil(meta.total / (meta.limit || 10)) : 0,
      },
    };
  },

  /**
   * Get product by ID
   */
  getById: async (id: string): Promise<Product> => {
    const response = await api.get<Product>(`/admin/products/${id}`);
    return response.data!;
  },

  /**
   * Create a new product
   */
  create: async (data: CreateProductInput): Promise<Product> => {
    const response = await api.post<Product>('/admin/products', data);
    return response.data!;
  },

  /**
   * Update a product
   */
  update: async (id: string, data: UpdateProductInput): Promise<Product> => {
    const response = await api.patch<Product>(`/admin/products/${id}`, data);
    return response.data!;
  },

  /**
   * Delete a product (soft delete)
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/products/${id}`);
  },

  /**
   * Toggle hot selling status
   */
  toggleHotSelling: async (id: string): Promise<Product> => {
    const response = await api.patch<Product>(`/admin/products/${id}/hot-selling`);
    return response.data!;
  },

  /**
   * Toggle product status
   */
  toggleStatus: async (id: string): Promise<Product> => {
    const response = await api.patch<Product>(`/admin/products/${id}/status`);
    return response.data!;
  },

  /**
   * Add images to a product
   */
  addImages: async (
    productId: string,
    images: Array<{
      imageUrl: string;
      isPrimary?: boolean;
      colorVariant?: string;
      displayOrder?: number;
    }>
  ): Promise<ProductImage[]> => {
    const response = await api.post<ProductImage[]>(`/admin/products/${productId}/images`, {
      images,
    });
    return response.data!;
  },

  /**
   * Delete an image
   */
  deleteImage: async (productId: string, imageId: string): Promise<void> => {
    await api.delete(`/admin/products/${productId}/images/${imageId}`);
  },

  /**
   * Set primary image
   */
  setPrimaryImage: async (productId: string, imageId: string): Promise<ProductImage> => {
    const response = await api.post<ProductImage>(
      `/admin/products/${productId}/images/${imageId}/primary`
    );
    return response.data!;
  },
};
